import { cn } from "@newxsystems/shared";
import { getComponent } from "../../../../client";
import { VelvetCommonContent } from "../../../../modules/content/components/VelvetCommonContent/VelvetCommonContent";
import { VelvetMedia } from "../../../../modules/content/components/VelvetMedia/VelvetMedia";
import { VelvetRichText } from "../../../../modules/content/components/VelvetRichText/VelvetRichText";
import { VelvetSection } from "../../../../modules/content/components/VelvetSection/VelvetSection";
import type { IFeaturedTestimonialBlockProps } from "./FeaturedTestimonialBlock.props";

export const FeaturedTestimonialBlock = ({
  alignment,
  anchorId,
  buttons,
  description,
  eyebrow,
  heading,
  parentClass,
  spacing,
  testimonial,
}: IFeaturedTestimonialBlockProps) => {
  if (!eyebrow && !heading && !description && !testimonial?.quote) return null;
  const Link = getComponent("Link");

  return (
    <VelvetSection className={parentClass} id={anchorId} spacing={spacing}>
      <div className="flex flex-col gap-16 md:gap-24">
        <VelvetCommonContent
          alignment={alignment}
          buttons={buttons}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
        />
        {testimonial ? (
          <figure className="relative grid border-y border-parchment/25 py-8 text-foreground md:min-h-[38rem] md:grid-cols-12 md:items-center md:py-12">
            {testimonial.portrait ? (
              <VelvetMedia
                aspectRatio="1:1"
                className="velvet-arch w-full border border-parchment/20 md:col-span-5 md:h-[34rem]"
                media={testimonial.portrait.image}
                sizes="(min-width: 768px) 35vw, 100vw"
              />
            ) : null}
            <div
              className={cn(
                "relative z-10 flex flex-col justify-center py-8 md:col-span-7 md:-ml-10 md:bg-ink/95 md:p-12 lg:p-16",
                !testimonial.portrait && "md:col-span-10 md:col-start-2 md:ml-0",
              )}
            >
              <span
                aria-hidden="true"
                className="mb-4 font-velvet-body text-7xl italic leading-none text-thread"
              >
                “
              </span>
              <blockquote>
                <VelvetRichText
                  className="max-w-[23ch] font-velvet-display text-4xl font-black uppercase leading-[0.95] tracking-[-0.035em] text-foreground md:text-6xl"
                  content={testimonial.quote}
                />
              </blockquote>
              <figcaption className="mt-8">
                <p className="font-velvet-body text-lg italic text-parchment">{testimonial.name}</p>
                {testimonial.attribution ? (
                  <p className="mt-2 font-velvet-mono text-xs text-smoke">
                    {testimonial.attribution}
                  </p>
                ) : null}
              </figcaption>
              {testimonial.link ? (
                <Link
                  aria-label={testimonial.link.accessibilityLabel || undefined}
                  className="mt-6 inline-flex min-h-11 items-center self-start font-velvet-mono text-xs text-ion-blue underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  href={testimonial.link.href}
                  rel={testimonial.link.openInNewTab ? "noopener noreferrer" : undefined}
                  target={testimonial.link.openInNewTab ? "_blank" : undefined}
                >
                  {testimonial.link.label}
                </Link>
              ) : null}
            </div>
          </figure>
        ) : null}
      </div>
    </VelvetSection>
  );
};

FeaturedTestimonialBlock.displayName = "Block::FeaturedTestimonialBlock";
